import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
    Plus, Trash, Star, Lightning, CaretLeft, CaretRight,
    Motorcycle, Bicycle, Upload, MapPin, GlobeHemisphereEast,
    Sliders, ArrowUp, TreeStructure, FloppyDisk
} from '@phosphor-icons/react';

import RidePrimitive from '../components/RidePrimitive';
import { VEHICLE_TYPES, vehicleLabel } from '../lib/vehicleTypes';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const VEHICLE_ICONS = {
    ebike: Bicycle, escooter: Motorcycle, electric_dirt_bike: Motorcycle,
    emoto: Motorcycle, dirt_bike: Motorcycle, skateboard: Lightning,
    electric_skateboard: Lightning, onewheel: Lightning, euc: Lightning,
    kart: Motorcycle, custom: Star,
};

const NEON_COLORS = ['#FF2020', '#1E90FF', '#00E64D', '#FFB400', '#9933FF', '#FF33CC', '#00BFFF'];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TERRAINS = [
    { id: 'land', label: 'Land', icon: MapPin },
    { id: 'air', label: 'Air', icon: GlobeHemisphereEast },
    { id: 'water', label: 'Water', icon: GlobeHemisphereEast },
];
const PROPULSIONS = [
    { id: 'kinetic', label: 'Kinetic' },
    { id: 'electric', label: 'Electric' },
    { id: 'gas', label: 'Gas' },
];

const getH = () => {
    const t = localStorage.getItem('btr_session_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function Garage() {
    const { user } = useAuth();
    const [rides, setRides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeIdx, setActiveIdx] = useState(0);
    const [showAdd, setShowAdd] = useState(false);
    const [newRide, setNewRide] = useState({ name: '', vehicle_type: 'ebike', terrain: 'land', propulsion: 'electric', description: '' });
    const [uploading, setUploading] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [imgFailed, setImgFailed] = useState(false);
    const [tuning, setTuning] = useState({});
    const [tuningParams, setTuningParams] = useState({});
    const [upgrades, setUpgrades] = useState({});
    const [upgradeCategories, setUpgradeCategories] = useState([]);
    const [diagrams, setDiagrams] = useState([]);
    const [savingTuning, setSavingTuning] = useState(false);
    const [buyingUpgrade, setBuyingUpgrade] = useState(null);
    const fileRef = useRef(null);
    const diagramRef = useRef(null);

    useEffect(() => { fetchRides(); fetchMeta(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchMeta = async () => {
        try {
            const [pRes, cRes] = await Promise.all([
                fetch(`${API}/garage/tuning-params`, { credentials: 'include', headers: getH() }),
                fetch(`${API}/garage/upgrade-categories`, { credentials: 'include', headers: getH() }),
            ]);
            if (pRes.ok) setTuningParams(await pRes.json());
            if (cRes.ok) setUpgradeCategories(await cRes.json());
        } catch (e) { console.error('Garage meta error:', e); }
    };

    const fetchRideDetails = async (rideId) => {
        try {
            const [tRes, uRes, dRes] = await Promise.all([
                fetch(`${API}/garage/rides/${rideId}/tuning`, { credentials: 'include', headers: getH() }),
                fetch(`${API}/garage/rides/${rideId}/upgrades`, { credentials: 'include', headers: getH() }),
                fetch(`${API}/garage/rides/${rideId}/diagrams`, { credentials: 'include', headers: getH() }),
            ]);
            if (tRes.ok) { const d = await tRes.json(); setTuning(d.params || {}); }
            if (uRes.ok) { const d = await uRes.json(); setUpgrades(d.levels || {}); }
            if (dRes.ok) setDiagrams(await dRes.json());
        } catch (e) { console.error('Garage ride details error:', e); }
    };

    useEffect(() => { setRotation(0); setImgFailed(false); if (rides[activeIdx]?.ride_id) fetchRideDetails(rides[activeIdx].ride_id); }, [activeIdx, rides]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchRides = async () => {
        try {
            const res = await fetch(`${API}/garage/rides`, { credentials: 'include', headers: getH() });
            if (res.ok) setRides(await res.json());
        } catch (e) { console.error('Garage rides error:', e); } finally { setLoading(false); }
    };

    const addRide = async () => {
        if (!newRide.name.trim()) { toast.error('Enter a ride name'); return; }
        const res = await fetch(`${API}/garage/rides`, {
            method: 'POST', headers: { ...getH(), 'Content-Type': 'application/json' },
            credentials: 'include', body: JSON.stringify(newRide)
        });
        if (res.ok) {
            toast.success('Ride added to garage!');
            setShowAdd(false);
            setNewRide({ name: '', vehicle_type: 'ebike', terrain: 'land', propulsion: 'electric', description: '' });
            fetchRides();
        } else {
            const err = await res.json();
            toast.error(err.detail || 'Failed to add');
        }
    };

    const deleteRide = async (id) => {
        const res = await fetch(`${API}/garage/rides/${id}`, { method: 'DELETE', credentials: 'include', headers: getH() });
        if (res.ok) { toast.success('Ride removed'); fetchRides(); setActiveIdx(0); }
    };

    const uploadPhoto = async (rideId, file) => {
        setUploading(true);
        const form = new FormData();
        form.append('file', file);
        try {
            const res = await fetch(`${API}/garage/rides/${rideId}/photo`, {
                method: 'POST', credentials: 'include', headers: getH(), body: form
            });
            if (res.ok) { toast.success('Photo uploaded!'); fetchRides(); }
            else toast.error('Upload failed');
        } catch (e) { toast.error('Upload failed'); }
        finally { setUploading(false); }
    };

    const saveTuning = async () => {
        if (!active) return;
        setSavingTuning(true);
        try {
            const res = await fetch(`${API}/garage/rides/${active.ride_id}/tuning`, {
                method: 'POST', headers: { ...getH(), 'Content-Type': 'application/json' },
                credentials: 'include', body: JSON.stringify({ params: tuning, preset_name: 'Custom' })
            });
            if (res.ok) toast.success('Tuning saved!');
            else toast.error('Save failed');
        } catch (e) { toast.error('Save failed'); }
        finally { setSavingTuning(false); }
    };

    const buyUpgrade = async (category) => {
        if (!active) return;
        setBuyingUpgrade(category);
        try {
            const res = await fetch(`${API}/garage/rides/${active.ride_id}/upgrades/${category}`, {
                method: 'POST', credentials: 'include', headers: getH()
            });
            if (res.ok) {
                const d = await res.json();
                toast.success(`${d.category} upgraded to Lv${d.new_level}! (-${d.cost} Boltz)`);
                fetchRideDetails(active.ride_id);
            } else {
                const e = await res.json();
                toast.error(e.detail || 'Upgrade failed');
            }
        } catch (e) { toast.error('Upgrade failed'); }
        finally { setBuyingUpgrade(null); }
    };

    const uploadDiagram = async (file) => {
        if (!active) return;
        const form = new FormData();
        form.append('file', file);
        form.append('title', file.name.replace(/\.[^.]+$/, ''));
        try {
            const res = await fetch(`${API}/garage/rides/${active.ride_id}/diagrams`, {
                method: 'POST', credentials: 'include', headers: getH(), body: form
            });
            if (res.ok) { toast.success('Diagram added!'); fetchRideDetails(active.ride_id); }
            else toast.error('Upload failed');
        } catch (e) { toast.error('Upload failed'); }
    };

    const deleteDiagram = async (id) => {
        const res = await fetch(`${API}/garage/diagrams/${id}`, { method: 'DELETE', credentials: 'include', headers: getH() });
        if (res.ok) { toast.success('Deleted'); fetchRideDetails(active.ride_id); }
    };

    const active = rides[activeIdx];

    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-6">
                    <div>
                        <span className="label-text text-plasma-red text-glow">3D GARAGE</span>
                        <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground">Your Rides</h1>
                        <p className="text-muted-foreground text-sm" style={{ fontFamily: 'var(--font-body)' }}>
                            {rides.length} ride{rides.length !== 1 ? 's' : ''} in garage
                        </p>
                    </div>
                    <Button onClick={() => setShowAdd(true)} className="bg-gradient-to-r from-plasma-red to-plasma-blue text-white font-display" data-testid="add-ride-btn">
                        <Plus size={18} className="mr-1" /> Add Ride
                    </Button>
                </motion.div>

                {/* Add Ride Modal */}
                <AnimatePresence>
                    {showAdd && (
                        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                            <Card className="glass-card mb-6 border-plasma-red/20">
                                <CardContent className="p-5 space-y-4">
                                    <Input value={newRide.name} onChange={e => setNewRide(p => ({ ...p, name: e.target.value }))} placeholder="Ride name (e.g. Sur-Ron Light Bee)" data-testid="ride-name-input" />
                                    <div className="grid grid-cols-3 gap-2">
                                        <div>
                                            <label className="text-xs text-muted-foreground font-display">Vehicle</label>
                                            <select value={newRide.vehicle_type} onChange={e => setNewRide(p => ({ ...p, vehicle_type: e.target.value }))} className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm" data-testid="vehicle-select">
                                                {VEHICLE_TYPES.map(v => (
                                                    <option key={v.id} value={v.id}>{v.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs text-muted-foreground font-display">Terrain</label>
                                            <select value={newRide.terrain} onChange={e => setNewRide(p => ({ ...p, terrain: e.target.value }))} className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm" data-testid="terrain-select">
                                                {TERRAINS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs text-muted-foreground font-display">Propulsion</label>
                                            <select value={newRide.propulsion} onChange={e => setNewRide(p => ({ ...p, propulsion: e.target.value }))} className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm" data-testid="propulsion-select">
                                                {PROPULSIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <Input value={newRide.description} onChange={e => setNewRide(p => ({ ...p, description: e.target.value }))} placeholder="Description (optional)" />
                                    <div className="flex gap-2">
                                        <Button onClick={addRide} className="flex-1 bg-neon-green text-black font-display" data-testid="confirm-add-ride">Add to Garage</Button>
                                        <Button onClick={() => setShowAdd(false)} variant="outline" className="border-white/10">Cancel</Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* 3D Ride Display */}
                {loading ? (
                    <Skeleton className="h-80 w-full rounded-2xl" />
                ) : rides.length === 0 ? (
                    <Card className="glass-card text-center py-16">
                        <Motorcycle size={64} className="text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground font-display">No rides yet</p>
                        <p className="text-sm text-muted-foreground mt-1">Add your first ride to get started</p>
                    </Card>
                ) : (
                    <>
                        {/* Holographic 3D Card */}
                        <motion.div
                            key={active?.ride_id}
                            initial={{ opacity: 0, rotateY: 15 }}
                            animate={{ opacity: 1, rotateY: 0 }}
                            transition={{ duration: 0.6 }}
                            className="relative mb-6"
                            style={{ perspective: '1000px' }}
                        >
                            <div
                                className="relative rounded-2xl overflow-hidden border"
                                style={{
                                    borderColor: active?.neon_color || '#FF2020',
                                    boxShadow: `0 0 30px ${active?.neon_color || '#FF2020'}22, 0 0 60px ${active?.neon_color || '#FF2020'}11, inset 0 0 30px ${active?.neon_color || '#FF2020'}08`,
                                    background: `linear-gradient(145deg, rgba(0,0,0,0.9), rgba(15,15,25,0.95))`,
                                    minHeight: '320px',
                                }}
                                data-testid="garage-3d-card"
                            >
                                {/* Holographic grid overlay */}
                                <div className="absolute inset-0 opacity-10" style={{
                                    backgroundImage: `linear-gradient(${active?.neon_color || '#FF2020'}40 1px, transparent 1px), linear-gradient(90deg, ${active?.neon_color || '#FF2020'}40 1px, transparent 1px)`,
                                    backgroundSize: '30px 30px',
                                }} />

                                {/* Plasma glow corners */}
                                <div className="absolute top-0 left-0 w-32 h-32 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${active?.neon_color || '#FF2020'}30, transparent)` }} />
                                <div className="absolute bottom-0 right-0 w-32 h-32 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(30,144,255,0.2), transparent)' }} />

                                {/* Photo or placeholder */}
                                <div className="relative z-10 flex flex-col items-center justify-center py-10 px-6">
                                    {(() => {
                                        const hasPhoto = active?.photos?.length > 0;
                                        const rawUrl = hasPhoto ? active.photos[0].url : null;
                                        const heroSrc = hasPhoto
                                            ? (rawUrl.startsWith('http') ? rawUrl : `${BACKEND}${rawUrl}`)
                                            : null;
                                        const neon = active?.neon_color || '#FF2020';
                                        return (
                                            <div className="w-full flex flex-col items-center">
                                                <div className="relative flex items-center justify-center w-full" style={{ perspective: '1200px' }}>
                                                    <button onClick={() => setRotation(r => r - 45)}
                                                        className="absolute left-0 z-20 w-9 h-9 rounded-full bg-black/40 hover:bg-black/70 border border-white/10 flex items-center justify-center text-white"
                                                        data-testid="garage-rotate-left" aria-label="Rotate left">
                                                        <CaretLeft size={18} />
                                                    </button>
                                                    {heroSrc && !imgFailed ? (
                                                        <img src={heroSrc} alt={active?.name}
                                                            data-testid="garage-hero-image" draggable={false}
                                                            onError={() => setImgFailed(true)}
                                                            className="max-h-52 max-w-[70%] object-contain select-none transition-transform duration-500"
                                                            style={{ transform: `rotateY(${rotation}deg)`, filter: `drop-shadow(0 0 22px ${neon}66)` }} />
                                                    ) : (
                                                        <RidePrimitive vehicleType={active?.vehicle_type} neon={neon}
                                                            rotation={rotation} label={vehicleLabel(active?.vehicle_type)} />
                                                    )}
                                                    <button onClick={() => setRotation(r => r + 45)}
                                                        className="absolute right-0 z-20 w-9 h-9 rounded-full bg-black/40 hover:bg-black/70 border border-white/10 flex items-center justify-center text-white"
                                                        data-testid="garage-rotate-right" aria-label="Rotate right">
                                                        <CaretRight size={18} />
                                                    </button>
                                                </div>
                                                <p className="text-[10px] text-muted-foreground mt-3">
                                                    {hasPhoto ? 'Tap arrows to rotate' : 'Stock preview · tap arrows to rotate'}
                                                </p>
                                                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                                                    className="mt-1.5 text-xs text-primary hover:underline flex items-center gap-1"
                                                    data-testid="garage-upload-photo">
                                                    <Upload size={13} /> {uploading ? 'Uploading…' : hasPhoto ? 'Replace photo' : 'Upload your own photo'}
                                                </button>
                                            </div>
                                        );
                                    })()}
                                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && uploadPhoto(active.ride_id, e.target.files[0])} />

                                    {/* Ride info */}
                                    <h2 className="text-2xl font-black font-display mt-6" style={{ color: active?.neon_color || '#FF2020', textShadow: `0 0 15px ${active?.neon_color || '#FF2020'}80` }} data-testid="ride-name">
                                        {active?.name}
                                    </h2>
                                    <div className="flex gap-2 mt-2">
                                        <Badge className="bg-white/5 border-white/10 text-xs">{active?.vehicle_type?.replace(/_/g, ' ')}</Badge>
                                        <Badge className="bg-plasma-red/10 border-plasma-red/20 text-plasma-red text-xs">{active?.terrain}</Badge>
                                        <Badge className="bg-plasma-blue/10 border-plasma-blue/20 text-plasma-blue text-xs">{active?.propulsion}</Badge>
                                    </div>
                                    {active?.description && <p className="text-sm text-muted-foreground mt-2 text-center">{active.description}</p>}
                                </div>

                                {/* Neon color selector */}
                                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                                    {NEON_COLORS.map(c => (
                                        <button key={c} onClick={async () => {
                                            await fetch(`${API}/garage/rides/${active.ride_id}`, {
                                                method: 'PATCH', headers: { ...getH(), 'Content-Type': 'application/json' },
                                                credentials: 'include', body: JSON.stringify({ neon_color: c })
                                            });
                                            fetchRides();
                                        }} className="w-4 h-4 rounded-full border border-white/20 transition-transform hover:scale-125" style={{ background: c, boxShadow: active?.neon_color === c ? `0 0 8px ${c}` : 'none' }} />
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        {/* Ride Carousel Nav */}
                        <div className="flex items-center justify-between mb-6">
                            <Button variant="ghost" size="sm" onClick={() => setActiveIdx(Math.max(0, activeIdx - 1))} disabled={activeIdx === 0}>
                                <CaretLeft size={20} />
                            </Button>
                            <div className="flex gap-2">
                                {rides.map((r, i) => (
                                    <button key={r.ride_id} onClick={() => setActiveIdx(i)}
                                        className={`w-2.5 h-2.5 rounded-full transition-all ${i === activeIdx ? 'scale-125' : 'opacity-40'}`}
                                        style={{ background: r.neon_color || '#FF2020' }}
                                    />
                                ))}
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setActiveIdx(Math.min(rides.length - 1, activeIdx + 1))} disabled={activeIdx >= rides.length - 1}>
                                <CaretRight size={20} />
                            </Button>
                        </div>

                        {/* Stats + Actions */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <Card className="glass-card">
                                <CardContent className="p-4 text-center">
                                    <p className="label-text text-primary">TOTAL KM</p>
                                    <p className="text-xl font-black font-display mt-1">{active?.stats?.total_km?.toFixed(1) || '0.0'}</p>
                                </CardContent>
                            </Card>
                            <Card className="glass-card">
                                <CardContent className="p-4 text-center">
                                    <p className="label-text text-electric-blue">RIDES</p>
                                    <p className="text-xl font-black font-display mt-1">{active?.stats?.total_rides || 0}</p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Tuning / Upgrades / Diagrams Tabs */}
                        <Tabs defaultValue="tuning" className="w-full mb-4">
                            <TabsList className="grid w-full grid-cols-3 h-auto mb-4">
                                <TabsTrigger value="tuning" className="text-xs py-2"><Sliders size={14} className="mr-1" />Tuning</TabsTrigger>
                                <TabsTrigger value="upgrades" className="text-xs py-2"><ArrowUp size={14} className="mr-1" />Upgrades</TabsTrigger>
                                <TabsTrigger value="diagrams" className="text-xs py-2"><TreeStructure size={14} className="mr-1" />Wiring</TabsTrigger>
                            </TabsList>

                            {/* Controller Tuning */}
                            <TabsContent value="tuning">
                                <Card className="glass-card">
                                    <CardContent className="p-4 space-y-4">
                                        <div className="flex justify-between items-center">
                                            <h3 className="font-display text-sm text-primary">CONTROLLER TUNING</h3>
                                            <Button size="sm" onClick={saveTuning} disabled={savingTuning} className="bg-primary text-black text-xs font-display" data-testid="save-tuning-btn">
                                                <FloppyDisk size={14} className="mr-1" />{savingTuning ? 'Saving...' : 'Save'}
                                            </Button>
                                        </div>
                                        {Object.entries(tuningParams).map(([key, spec]) => (
                                            <div key={key}>
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className="text-muted-foreground">{spec.label}</span>
                                                    <span className="font-mono text-foreground">{tuning[key] ?? spec.default} {spec.unit}</span>
                                                </div>
                                                <input type="range" min={spec.min} max={spec.max} step={spec.step}
                                                    value={tuning[key] ?? spec.default}
                                                    onChange={e => setTuning(p => ({ ...p, [key]: Number(e.target.value) }))}
                                                    className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                                                    style={{ background: `linear-gradient(to right, #00FF8A ${((tuning[key] ?? spec.default) - spec.min) / (spec.max - spec.min) * 100}%, rgba(255,255,255,0.1) 0%)` }}
                                                    data-testid={`tuning-${key}`}
                                                />
                                            </div>
                                        ))}
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* Performance Upgrades */}
                            <TabsContent value="upgrades">
                                <div className="space-y-2">
                                    {upgradeCategories.map(cat => {
                                        const lvl = upgrades[cat.id] || 0;
                                        const maxed = lvl >= cat.levels;
                                        const cost = cat.base_cost * (lvl + 1);
                                        return (
                                            <Card key={cat.id} className="glass-card">
                                                <CardContent className="p-3 flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-cyber-purple/10 flex items-center justify-center shrink-0">
                                                        <ArrowUp size={18} className="text-cyber-purple" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-sm font-bold">{cat.label}</p>
                                                        <div className="flex gap-1 mt-1">
                                                            {Array.from({ length: cat.levels }, (_, i) => (
                                                                <div key={i} className={`w-5 h-1.5 rounded-full ${i < lvl ? 'bg-primary' : 'bg-white/10'}`} />
                                                            ))}
                                                        </div>
                                                    </div>
                                                    {maxed ? (
                                                        <Badge className="bg-primary/10 text-primary text-[10px]">MAX</Badge>
                                                    ) : (
                                                        <Button size="sm" onClick={() => buyUpgrade(cat.id)} disabled={buyingUpgrade === cat.id}
                                                            className="text-xs bg-cyber-purple text-white font-display" data-testid={`upgrade-${cat.id}`}>
                                                            {buyingUpgrade === cat.id ? '...' : `${cost} Boltz`}
                                                        </Button>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </TabsContent>

                            {/* Wiring Diagrams */}
                            <TabsContent value="diagrams">
                                <Card className="glass-card">
                                    <CardContent className="p-4 space-y-3">
                                        <div className="flex justify-between items-center">
                                            <h3 className="font-display text-sm text-electric-blue">WIRING DIAGRAMS</h3>
                                            <Button size="sm" variant="outline" className="text-xs border-electric-blue/30 text-electric-blue" onClick={() => diagramRef.current?.click()} data-testid="upload-diagram-btn">
                                                <Upload size={14} className="mr-1" />Add
                                            </Button>
                                            <input ref={diagramRef} type="file" accept="image/*,.pdf" className="hidden" onChange={e => e.target.files[0] && uploadDiagram(e.target.files[0])} />
                                        </div>
                                        {diagrams.length === 0 ? (
                                            <p className="text-xs text-muted-foreground text-center py-4">No diagrams yet. Upload wiring diagrams for your ride.</p>
                                        ) : (
                                            <div className="grid grid-cols-2 gap-2">
                                                {diagrams.map(d => (
                                                    <div key={d.diagram_id} className="relative group rounded-lg overflow-hidden border border-white/5">
                                                        <img src={d.url} alt={d.title} className="w-full h-24 object-cover" />
                                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                            <p className="text-xs text-white font-display">{d.title}</p>
                                                            <button onClick={() => deleteDiagram(d.diagram_id)} className="text-destructive text-xs"><Trash size={14} /></button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </Tabs>

                        {/* Delete button */}
                        <div className="mt-4 text-center">
                            <Button variant="ghost" size="sm" className="text-destructive text-xs" onClick={() => { if (window.confirm('Remove this ride?')) deleteRide(active.ride_id); }} data-testid="delete-ride-btn">
                                <Trash size={14} className="mr-1" /> Remove from Garage
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}
